import { AutomationModelBase } from '../../../tamalelibs/models/automation-base.model';
import { GatewayType, TaskType } from '../../../tamalelibs/models/workflow.model';
import { DiagramContextMenu, DiagramContextMenuActionType } from '../diagram-context-menu/diagram-context-menu.model';
import { NodeEventType, START_NODE } from '../diagram.model';
import { DiagramNodesList } from './diagram-nodes-list';
export const GatewaySuccessorCondition = 'none';
export enum BranchType {
    NormalBranch = 'normal_branch',
    ConditionalBranch = 'conditional_branch',
}

export class DiagramBranch {
    automationConfigs?: Array<AutomationModelBase>;
    id?: string;
    guid?: string;
    name?: string;
    type?: BranchType;
    active?: boolean;
    condition?: any;
    branchs?: Array<DiagramBranch>;
    parents?: Array<any>;
    children?: Array<any>;
    showInput?: boolean;
    conditionDisplayLines?: Array<{ field: string, operator: string, expectedValue: string }>;

    /**
     * determine whether a condition is blank
     *
     * @static
     * @param {*} condition
     * @return {*}
     * @memberof DiagramBranch
     */
    static isBlankCondition(condition: any) {
        return !condition || condition.toString().toLowerCase() === 'true' || condition === GatewaySuccessorCondition;
    }

    /**
     * determines whether the given condition belongs to a successor of a
     * @param condition the conditon of potential successor nodes of gateways.
     * @param parentGatewayId the id of parent gateway.
     * @returns A flag indicating whether the given condition belongs to a successor of a gateway.
     */
    static isSuccessorCondition(condition: string, parentGatewayId: string) {
        return condition === GatewaySuccessorCondition || (condition && condition.includes(parentGatewayId));
    }
}

const DIAGRAM_NODE_PROPERTY_MAPPING = { 'automation': 'automationConfigs' };

export class DiagramNode {
    guid: string;
    parents: Set<string>;
    parentBranchID?: string;
    children: Set<string>;
    name: string;
    type: GatewayType | TaskType;
    diagramContextMenu = new DiagramContextMenu();
    condition?: string;
    automationConfigs?: Array<AutomationModelBase>;
    branchs?: Array<DiagramBranch>;
    active?: boolean;
    gatewayConfig?: any;
    id: string;
    valid: boolean;
    showInput?: boolean;
    stage: string;
    stageColor?: number;

    static getNoneCondition(): string {
        return GatewaySuccessorCondition;
    }

    static isNonBranchCondition(condition: string): boolean {
        return !condition || condition.trim().length === 0 || DiagramNode.getNoneCondition() === condition.toLowerCase();
    }

    constructor(
        props,
        public getThisNodeList: () => DiagramNodesList
    ) {
        if (!props.guid) {
            return;
        }
        for (const prop in props) {
            if (props.hasOwnProperty(prop)) {
                const localPropName = DIAGRAM_NODE_PROPERTY_MAPPING[prop] ? DIAGRAM_NODE_PROPERTY_MAPPING[prop] : prop;
                this[localPropName] = props[prop];
            }
        }
        this.type = props.type || GatewayType.PARALLEL;
        this.diagramContextMenu = props.diagramContextMenu;
        this.children = new Set(props.children as string[]);
        this.parents = new Set(props.parents as string[]);
    }

    addChild(event, condition?) {
        event.stopPropagation();
        this.diagramContextMenu.actions$.next({
            type: DiagramContextMenuActionType.LoadData,
            payload: {
                anchor: event.target,
                targetId: this.guid,
                condition: condition,
                event: event
            }
        });
    }

    /**
     * calls up the context menu for adding a child node to a gateway branch.
     * @param event the event arguments
     * @param branchId the id of the branch to add the new child node.
     * @param isTriggerOnBranch a flag indicating whether it's called right on a branch rather than a branch's child node.
     */
    addBranchChild(event, branchId, isTriggerOnBranch = false) {
        event.stopPropagation();
        const pl = {
            anchor: event.target,
            targetId: this.guid,
            condition: branchId,
            isTriggerOnBranch: isTriggerOnBranch,
            // a flag indicating this context menu is for adding a child to a branch.
            isAddToBranch: true,
            parentBranch: null,
            event:event
        };
        if (!DiagramBranch.isBlankCondition(branchId)) {
            pl.parentBranch = branchId;
        }
       
        this.diagramContextMenu.actions$.next({
            type: DiagramContextMenuActionType.LoadData,
            payload: pl
        });
    }

    /**
     * calls up the context menu for adding a sibling right after current gateway node.
     * @param event the event context
     */
    addGatewaySuccessor(event) {
        event.stopPropagation();
        let parentBranchId = !DiagramBranch.isBlankCondition(this.condition) ? this.condition : null;
        if (!parentBranchId && !DiagramBranch.isBlankCondition(this.parentBranchID)) {
            parentBranchId = this.parentBranchID;
        }
        this.diagramContextMenu.actions$.next({
            type: DiagramContextMenuActionType.LoadData,
            payload: {
                anchor: event.target,
                targetId: this.guid,
                condition: GatewaySuccessorCondition,
                parentBranch: parentBranchId,
                isGatewaySuccessor: true,
                event: event
            }
        });
    }

    addCondition(event) {
        event.stopPropagation();
        const nodeList = this.getThisNodeList();
        const branch = nodeList.newBranchNode(this);
        this.branchs.push(branch);
        nodeList.calculateNodeWrapWidth(this);
    }

    isRoot() {
        return this.parents.has(START_NODE.guid);
    }

    destory(event, branch?, service?) {
        event.stopPropagation();
        if (branch) {
            this.getThisNodeList().deleteBranch(this.guid, branch.guid, service);
        } else {
            this.getThisNodeList().deleteNode(this.guid);
        }
    }

    hasChildren() {
        return !!this.children.size;
    }

    hasParents() {
        return !!this.parents.size;
    }

    getConditionNodes(condition) {
        const conNodes = [];
        this.children.forEach(item => {
            const node = this.getThisNodeList().getNode(item);
            if (node.condition && node.condition === condition) {
                conNodes.push(item);
            }
        });
        return conNodes;
    }

    getGatewaySuccessors() {
        return this.getConditionNodes(GatewaySuccessorCondition);
    }

    serializeNode() {
        const branchs = [];
        if (this.branchs && this.branchs.length > 0) {
            this.branchs.forEach(item => {
                branchs.push({
                    id: item.id,
                    guid: item.guid,
                    name: item.name,
                    type: item.type,
                    active: item.active,
                    condition: item.condition,
                    branchs: item.branchs,
                    parents: Array.from(item.parents || new Map()),
                    children: Array.from(item.children || new Map()),
                    showInput: item.showInput,
                });
            });
        }
        const json = {
            guid: this.guid,
            name: this.name,
            type: this.type,
            id: this.id,
            condition: this.condition,
            branchs: branchs,
            active: this.active,
            valid: this.valid,
            gatewayConfig: this.gatewayConfig,
            parents: Array.from(this.parents),
            children: Array.from(this.children),
            showInput: this.showInput,
        };
        return json;
    }

    blurNodeEventEmit(service) {
        service.diagramConfig.events$.next({
            type: NodeEventType.blurNode,
            payload: this.serializeNode()
        });
    }

    clickNodeEventEmit(service) {
        service.diagramConfig.events$.next({
            type: NodeEventType.clickNode,
            payload: this.serializeNode()
        });
    }

    clickNoteMeatballEventEmit(event, service) {
        service.diagramConfig.events$.next({
            type: NodeEventType.clickNoteMeatball,
            payload: {
                event: event,
                node: this.serializeNode()
            }
        });
    }

    addNodeEventEmit(service, node) {
        service.diagramConfig.events$.next({
            type: NodeEventType.addkNode,
            payload: node
        });
    }

    showSlideSheetEmit(service) {
        service.diagramConfig.events$.next({
            type: NodeEventType.enterNode,
            payload: this.serializeNode()
        });
    }
}
